'use client'

import { motion } from 'motion/react'
import { Fragment, forwardRef, type PropsWithChildren } from 'react'
import { Radio } from '@headlessui/react'

import { cn } from '@mntn-dev/ui-utilities'

import { getTestProps, type TestIds } from '../../utils/testing.ts'
import type { ButtonProps } from '../buttons/button.tsx'
import { getIconSize } from '../buttons/styles.ts'
import { Icon } from '../icon/icon.tsx'
import type { IconName } from '../icon/types/names.ts'
import {
  getRadioButtonClassName,
  type RadioButtonStyleProps,
  type RadioButtonTheme,
} from './styles.ts'

export type RadioButtonsItemProps = Readonly<
  PropsWithChildren<
    TestIds &
      Pick<RadioButtonStyleProps, 'theme'> &
      Pick<ButtonProps, 'size' | 'disabled'> & {
        className?: string
        value: string
      }
  >
>

const radioButtonThemeIconMap: Record<RadioButtonTheme, IconName> = {
  positive: 'check',
  info: 'information',
}

const MotionIcon = motion(Icon)

export const RadioButton = forwardRef<HTMLDivElement, RadioButtonsItemProps>(
  (
    {
      children,
      className,
      dataTestId,
      dataTrackingId,
      disabled,
      size = 'md',
      theme,
      value,
      ...buttonProps
    },
    ref
  ) => {
    return (
      <Radio as={Fragment} value={value} disabled={disabled}>
        {({ checked }) => (
          <div
            {...getTestProps({
              dataTestId,
              dataTrackingId,
            })}
            ref={ref}
            className={cn(
              getRadioButtonClassName({
                size,
                theme,
              }),
              'aria-disabled:opacity-30 aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed',
              className
            )}
            {...buttonProps}
          >
            <MotionIcon
              initial={false}
              animate={{
                opacity: checked ? 1 : 0,
                scale: checked ? 1 : 0,
                width: checked ? '1rem' : 0,
                height: checked ? '1rem' : 0,
              }}
              transition={{
                type: 'spring',
                stiffness: 500,
                damping: 25,
              }}
              name={radioButtonThemeIconMap[theme]}
              size={getIconSize(size)}
              color="current"
            />
            {children}
          </div>
        )}
      </Radio>
    )
  }
)
